define('@ember-data/adapter/-private', ['exports', '@ember/debug', 'rsvp', 'require', '@ember/object/mixin', '@ember/string', 'ember-inflector'], (function (exports, debug, rsvp, require, Mixin, string, emberInflector) { 'use strict';

  function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

  var require__default = /*#__PURE__*/_interopDefaultLegacy(require);
  var Mixin__default = /*#__PURE__*/_interopDefaultLegacy(Mixin);

  const newline = /\r?\n/;
  function parseResponseHeaders(headersString) {
    let headers = Object.create(null);
    if (!headersString) {
      return headers;
    }
    let headerPairs = headersString.split(newline);
    for (let i = 0; i < headerPairs.length; i++) {
      let header = headerPairs[i];
      let j = 0;
      let foundSep = false;
      for (; j < header.length; j++) {
        if (header.charCodeAt(j) === 58 /* ':' */) {
          foundSep = true;
          break;
        }
      }
      if (foundSep === false) {
        continue;
      }
      let field = header.substring(0, j).trim();
      let value = header.substring(j + 1, header.length).trim();
      if (value) {
        let lowerCasedField = field.toLowerCase();
        headers[lowerCasedField] = value;
        headers[field] = value;
      }
    }
    return headers;
  }

  function continueOnReject(promise) {
    return rsvp.resolve(promise).catch(e => e);
  }

  /*
   * Function that always attempts to parse the response as json, and if an error is thrown,
   * returns `undefined` if the response is successful and has a status code of 204 (No Content),
   * or 205 (Reset Content) or if the request method was 'HEAD', and the plain payload otherwise.
   */
  function _determineContent(response, requestData, payload) {
    let ret = payload;
    let error = null;
    if (!response.ok) {
      return payload;
    }
    let status = response.status;
    let payloadIsEmpty = payload === '' || payload === null;
    let statusIndicatesEmptyResponse = status === 204 || status === 205 || requestData.method === 'HEAD';
    if (response.ok && (statusIndicatesEmptyResponse || payloadIsEmpty)) {
      return;
    }
    try {
      ret = JSON.parse(payload);
    } catch (e) {
      if (!(e instanceof SyntaxError)) {
        return e;
      }
      e.payload = payload;
      error = e;
    }
    if (error) {
      return error;
    }
    return ret;
  }
  function determineBodyPromise(response, requestData) {
    // response.text() may resolve or reject
    // it is a native promise, may not have finally
    return continueOnReject(response.text()).then(payload => _determineContent(response, requestData, payload));
  }

  const RBRACKET = /\[\]$/;
  function isPlainObject(obj) {
    return Object.prototype.toString.call(obj) === '[object Object]';
  }

  /*
   * Helper function that turns the data/body of a request into a query param string.
   * This is directly copied from jQuery.param.
   */
  function serializeQueryParams(queryParamsObject) {
    let s = [];
    function buildParams(prefix, obj) {
      let i, len, key;
      if (prefix) {
        if (Array.isArray(obj)) {
          for (i = 0, len = obj.length; i < len; i++) {
            if (RBRACKET.test(prefix)) {
              add(s, prefix, obj[i]);
            } else {
              buildParams(prefix + '[' + (typeof obj[i] === 'object' ? i : '') + ']', obj[i]);
            }
          }
        } else if (isPlainObject(obj)) {
          for (key in obj) {
            buildParams(prefix + '[' + key + ']', obj[key]);
          }
        } else {
          add(s, prefix, obj);
        }
      } else if (Array.isArray(obj)) {
        for (i = 0, len = obj.length; i < len; i++) {
          add(s, obj[i].name, obj[i].value);
        }
      } else {
        for (key in obj) {
          buildParams(key, obj[key]);
        }
      }
      return s;
    }
    return buildParams('', queryParamsObject).join('&');
  }

  /*
   * Part of the `serializeQueryParams` helper function.
   */
  function add(s, k, v) {
    // Strip out keys with undefined value and replace null values with
    // empty strings (mimics jQuery.ajax)
    if (v === undefined) {
      return;
    } else if (v === null) {
      v = '';
    }
    v = typeof v === 'function' ? v() : v;
    s[s.length] = `${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
  }

  let _fetch = null;
  function getFetchFunction() {
    if (_fetch !== null) {
      return _fetch();
    }
    if (require.has('fetch')) {
      // use `fetch` module by default, this is commonly provided by ember-fetch
      let fetchFn = require__default["default"]('fetch').default;
      _fetch = () => fetchFn;
    } else if (typeof fetch === 'function') {
      // fallback to using global fetch
      _fetch = () => fetch;
    } else {
      throw new Error('cannot find the `fetch` module or the `fetch` global. Did you mean to install the `ember-fetch` addon?');
    }
    return _fetch();
  }

  /**
    @module @ember-data/adapter
  */

  /*
   The structure of this file is such because typing Mixins is hard. Here we've structured it in
   such a way as to maximize the type information that a consumer can utilize. There are simpler
   ways to type a mixin but we would not be able to provide the nice overload signature for buildURL
  */
  // the interface must fully declare the function signatures that the individual functions
  // will also declare. If instead we try to keep them in sync by doing something like
  // `interface BuildURLMixin { buildURL: typeof buildURL }`
  // then an extending class overwriting one of the methods will break because typescript
  // thinks it is a switch from an instance prop (that is a method) to an instance method.
  // prevents the final constructed object from needing to add
  // host and namespace which are provided by the final consuming
  // class to the prototype which can result in overwrite errors
  /**
    ## Using BuildURLMixin

    To use URL building, include the mixin when extending an adapter, and call `buildURL` where needed.
    The default behaviour is designed for RESTAdapter.

    ### Example

    ```javascript
    import Adapter, { BuildURLMixin } from '@ember-data/adapter';

    export default class ApplicationAdapter extends Adapter.extend(BuildURLMixin) {
      findRecord(store, type, id, snapshot) {
        var url = this.buildURL(type.modelName, id, snapshot, 'findRecord');
        return this.ajax(url, 'GET');
      }
    }
    ```

    ### Attributes

    The `host` and `namespace` attributes will be used if defined, and are optional.

    @class BuildURLMixin
    @public
  */
  /**
      Builds a URL for a given type and optional ID.

      By default, it pluralizes the type's name (for example, 'post'
      becomes 'posts' and 'person' becomes 'people'). To override the
      pluralization see [pathForType](./pathForType?anchor=pathForType).

      If an ID is specified, it adds the ID to the path generated
      for the type, separated by a `/`.

      When called by `RESTAdapter.findMany()` the `id` and `snapshot` parameters
      will be arrays of ids and snapshots.

      @method buildURL
      @public
      @param {String} modelName
      @param {(String|Array|Object)} id single id or array of ids or query
      @param {(Snapshot|SnapshotRecordArray)} snapshot single snapshot or array of snapshots
      @param {String} requestType
      @param {Object} query object of query parameters to send for query requests.
      @return {String} url
    */
  function buildURL(modelName, id, snapshot, requestType, query) {
    /*
        Switch statements in typescript don't currently narrow even when the function is implemented
        with overloads.
         We still extract this to stand alone so that we can provide nice overloads for calling signatures,
        but we will still require all of this casting (or a ridiculous number of assertsthat narrow it down
        for us).
    */
    switch (requestType) {
      case 'findRecord':
        return this.urlForFindRecord(id, modelName, snapshot);
      case 'findAll':
        return this.urlForFindAll(modelName, snapshot);
      case 'query':
        return this.urlForQuery(query || {}, modelName);
      case 'queryRecord':
        return this.urlForQueryRecord(query || {}, modelName);
      case 'findMany':
        return this.urlForFindMany(id, modelName, snapshot);
      case 'findHasMany':
        return this.urlForFindHasMany(id, modelName, snapshot);
      case 'findBelongsTo':
        return this.urlForFindBelongsTo(id, modelName, snapshot);
      case 'createRecord':
        return this.urlForCreateRecord(modelName, snapshot);
      case 'updateRecord':
        return this.urlForUpdateRecord(id, modelName, snapshot);
      case 'deleteRecord':
        return this.urlForDeleteRecord(id, modelName, snapshot);
      default:
        // this is the 'never' case but someone may call `buildURL` manually so
        // we try to do something for them.
        return this._buildURL(modelName, id);
    }
  }

  /**
      @method _buildURL
      @private
      @param {String} modelName
      @param {String} id
      @return {String} url
    */
  function _buildURL(modelName, id) {
    let path;
    let url = [];
    let {
      host
    } = this;
    let prefix = this.urlPrefix();
    if (modelName) {
      path = this.pathForType(modelName);
      if (path) {
        url.push(path);
      }
    }
    if (id) {
      url.push(encodeURIComponent(id));
    }
    if (prefix) {
      url.unshift(prefix);
    }
    let urlString = url.join('/');
    if (!host && urlString && urlString.charAt(0) !== '/') {
      urlString = '/' + urlString;
    }
    return urlString;
  }

  /**
     Builds a URL for a `store.findRecord(type, id)` call.

     Example:

     ```app/adapters/user.js
     import JSONAPIAdapter from '@ember-data/adapter/json-api';

     export default class ApplicationAdapter extends JSONAPIAdapter {
       urlForFindRecord(id, modelName, snapshot) {
         let baseUrl = this.buildURL(modelName, id, snapshot);
         return `${baseUrl}/users/${snapshot.adapterOptions.user_id}/playlists/${id}`;
       }
     }
     ```

     @method urlForFindRecord
     @public
     @param {String} id
     @param {String} modelName
     @param {Snapshot} snapshot
     @return {String} url

     */
  function urlForFindRecord(id, modelName, snapshot) {
    return this._buildURL(modelName, id);
  }

  /**
     Builds a URL for a `store.findAll(type)` call.

     Example:

     ```app/adapters/comment.js
     import JSONAPIAdapter from '@ember-data/adapter/json-api';

     export default class ApplicationAdapter extends JSONAPIAdapter {
       urlForFindAll(modelName, snapshot) {
         let baseUrl = this.buildURL(modelName);
         return `${baseUrl}/data/comments.json`;
       }
     }
     ```

     @method urlForFindAll
      @public
     @param {String} modelName
     @param {SnapshotRecordArray} snapshot
     @return {String} url
     */
  function urlForFindAll(modelName, snapshots) {
    return this._buildURL(modelName);
  }

  /**
     Builds a URL for a `store.query(type, query)` call.

     Example:

     ```app/adapters/application.js
     import RESTAdapter from '@ember-data/adapter/rest';

     export default class ApplicationAdapter extends RESTAdapter {
       host = 'https://api.github.com';
       urlForQuery (query, modelName) {
         switch(modelName) {
           case 'repo':
             return `https://api.github.com/orgs/${query.orgId}/repos`;
           default:
             return super.urlForQuery(...arguments);
         }
       }
     }
     ```

     @method urlForQuery
      @public
     @param {Object} query
     @param {String} modelName
     @return {String} url
     */
  function urlForQuery(query, modelName) {
    return this._buildURL(modelName);
  }

  /**
     Builds a URL for a `store.queryRecord(type, query)` call.

     Example:

     ```app/adapters/application.js
     import RESTAdapter from '@ember-data/adapter/rest';

     export default class ApplicationAdapter extends RESTAdapter {
       urlForQueryRecord({ slug }, modelName) {
         let baseUrl = this.buildURL();
         return `${baseUrl}/${encodeURIComponent(slug)}`;
       }
     }
     ```

     @method urlForQueryRecord
      @public
     @param {Object} query
     @param {String} modelName
     @return {String} url
     */
  function urlForQueryRecord(query, modelName) {
    return this._buildURL(modelName);
  }

  /**
     Builds a URL for coalescing multiple `store.findRecord(type, id)`
     records into 1 request when the adapter's `coalesceFindRequests`
     property is `true`.

     Example:

     ```app/adapters/application.js
     import RESTAdapter from '@ember-data/adapter/rest';

     export default class ApplicationAdapter extends RESTAdapter {
       urlForFindMany(ids, modelName) {
         let baseUrl = this.buildURL();
         return `${baseUrl}/coalesce`;
       }
     }
     ```

     @method urlForFindMany
      @public
     @param {Array} ids
     @param {String} modelName
     @param {Array} snapshots
     @return {String} url
     */
  function urlForFindMany(ids, modelName, snapshots) {
    return this._buildURL(modelName);
  }

  /**
     Builds a URL for fetching an async `hasMany` relationship when a URL
     is not provided by the server.

     Example:

     ```app/adapters/application.js
     import JSONAPIAdapter from '@ember-data/adapter/json-api';

     export default class ApplicationAdapter extends JSONAPIAdapter {
       urlForFindHasMany(id, modelName, snapshot) {
         let baseUrl = this.buildURL(modelName, id);
         return `${baseUrl}/relationships`;
       }
     }
     ```

     @method urlForFindHasMany
      @public
     @param {String} id
     @param {String} modelName
     @param {Snapshot} snapshot
     @return {String} url
     */
  function urlForFindHasMany(id, modelName, snapshot) {
    return this._buildURL(modelName, id);
  }

  /**
     Builds a URL for fetching an async `belongsTo` relationship when a url
     is not provided by the server.

     Example:

     ```app/adapters/application.js
     import JSONAPIAdapter from '@ember-data/adapter/json-api';

     export default class ApplicationAdapter extends JSONAPIAdapter {
       urlForFindBelongsTo(id, modelName, snapshot) {
         let baseUrl = this.buildURL(modelName, id);
         return `${baseUrl}/relationships`;
       }
     }
     ```

     @method urlForFindBelongsTo
      @public
     @param {String} id
     @param {String} modelName
     @param {Snapshot} snapshot
     @return {String} url
     */
  function urlForFindBelongsTo(id, modelName, snapshot) {
    return this._buildURL(modelName, id);
  }

  /**
     Builds a URL for a `record.save()` call when the record was created
     locally using `store.createRecord()`.

     Example:

     ```app/adapters/application.js
     import RESTAdapter from '@ember-data/adapter/rest';

     export default class ApplicationAdapter extends RESTAdapter {
       urlForCreateRecord(modelName, snapshot) {
         return super.urlForCreateRecord(...arguments) + '/new';
       }
     }
     ```

     @method urlForCreateRecord
      @public
     @param {String} modelName
     @param {Snapshot} snapshot
     @return {String} url
     */
  function urlForCreateRecord(modelName, snapshot) {
    return this._buildURL(modelName);
  }

  /**
     Builds a URL for a `record.save()` call when the record has been updated locally.

     Example:

     ```app/adapters/application.js
     import RESTAdapter from '@ember-data/adapter/rest';

     export default class ApplicationAdapter extends RESTAdapter {
       urlForUpdateRecord(id, modelName, snapshot) {
         return `/${id}/feed?access_token=${snapshot.adapterOptions.token}`;
       }
     }
     ```

     @method urlForUpdateRecord
      @public
     @param {String} id
     @param {String} modelName
     @param {Snapshot} snapshot
     @return {String} url
     */
  function urlForUpdateRecord(id, modelName, snapshot) {
    return this._buildURL(modelName, id);
  }

  /**
     Builds a URL for a `record.save()` call when the record has been deleted locally.

     Example:

     ```app/adapters/application.js
     import RESTAdapter from '@ember-data/adapter/rest';

     export default class ApplicationAdapter extends RESTAdapter {
       urlForDeleteRecord(id, modelName, snapshot) {
         return super.urlForDeleteRecord(...arguments) + '/destroy';
       }
     }
     ```

     @method urlForDeleteRecord
      @public
     @param {String} id
     @param {String} modelName
     @param {Snapshot} snapshot
     @return {String} url
     */
  function urlForDeleteRecord(id, modelName, snapshot) {
    return this._buildURL(modelName, id);
  }

  /**
      @method urlPrefix
      @private
      @param {String} path
      @param {String} parentURL
      @return {String} urlPrefix
    */
  function urlPrefix(path, parentURL) {
    let {
      host,
      namespace
    } = this;
    if (!host || host === '/') {
      host = '';
    }
    if (path) {
      // Protocol relative url
      if (/^\/\//.test(path) || /http(s)?:\/\//.test(path)) {
        // Do nothing, the full host is already included.
        return path;

        // Absolute path
      } else if (path.charAt(0) === '/') {
        return `${host}${path}`;
        // Relative path
      } else {
        return `${parentURL}/${path}`;
      }
    }

    // No path provided
    let url = [];
    if (host) {
      url.push(host);
    }
    if (namespace) {
      url.push(namespace);
    }
    return url.join('/');
  }

  /**
      Determines the pathname for a given type.

      By default, it pluralizes the type's name (for example,
      'post' becomes 'posts' and 'person' becomes 'people').

      ### Pathname customization

      For example, if you have an object `LineItem` with an
      endpoint of `/line_items/`.

      ```app/adapters/application.js
      import RESTAdapter from '@ember-data/adapter/rest';
      import { decamelize } from '@ember/string';
      import { pluralize } from 'ember-inflector';

      export default class ApplicationAdapter extends RESTAdapter {
        pathForType(modelName) {
          var decamelized = decamelize(modelName);
          return pluralize(decamelized);
        }
      }
      ```

      @method pathForType
      @public
      @param {String} modelName
      @return {String} path
    **/
  function pathForType(modelName) {
    let camelized = string.camelize(modelName);
    return emberInflector.pluralize(camelized);
  }

  // we build it this way vs casting to BuildURLMixin so that any
  // changes to the interface surface as errors here.
  const mixinProps = {
    buildURL,
    _buildURL,
    urlForFindRecord,
    urlForFindAll,
    urlForQueryRecord,
    urlForQuery,
    urlForFindMany,
    urlForFindHasMany,
    urlForFindBelongsTo,
    urlForCreateRecord,
    urlForDeleteRecord,
    urlForUpdateRecord,
    urlPrefix,
    pathForType
  };
  var buildUrlMixin = Mixin__default["default"].create(mixinProps);

  function serializeIntoHash(store, modelClass, snapshot, options) {
    if (options === void 0) {
      options = {
        includeId: true
      };
    }
    const serializer = store.serializerFor(modelClass.modelName);
    if (typeof serializer.serializeIntoHash === 'function') {
      const data = {};
      serializer.serializeIntoHash(data, modelClass, snapshot, options);
      return data;
    }
    return serializer.serialize(snapshot, options);
  }

  exports.BuildURLMixin = buildUrlMixin;
  exports.determineBodyPromise = determineBodyPromise;
  exports.fetch = getFetchFunction;
  exports.parseResponseHeaders = parseResponseHeaders;
  exports.serializeIntoHash = serializeIntoHash;
  exports.serializeQueryParams = serializeQueryParams;

  Object.defineProperty(exports, '__esModule', { value: true });

}));
